/**
 * @fileoverview Caso de uso para analíticas - Orquesta operaciones relacionadas con reportes y dashboards
 * @module application/use-cases/AnalyticsUseCase
 * @requires domain/entities/AnalyticsReport
 */

import AnalyticsReport from '../../domain/entities/AnalyticsReport';

/**
 * @class AnalyticsUseCase
 * @description Implementa la lógica de negocio para analíticas y reportes
 * Orquesta operaciones entre dominio e infraestructura siguiendo patrón Application Service
 */
export default class AnalyticsUseCase {
  /**
   * @constructor
   * @param {IAnalyticsRepository} analyticsRepository - Repositorio de analíticas
   * @param {Object} logger - Sistema de logging estructurado
   * @param {Object} cache - Sistema de cache para optimizar consultas frecuentes
   */
  constructor(analyticsRepository, logger, cache = null) {
    this.analyticsRepository = analyticsRepository;
    this.logger = logger || console;
    this.cache = cache;
    
    this.logger.info('[AnalyticsUseCase] Inicializado');
  }
  
  /**
   * Obtiene los datos para un dashboard específico con optimización de caché
   * @param {string} dashboardId - Identificador del dashboard
   * @param {Object} params - Parámetros de filtrado y personalización
   * @param {string} userId - ID del usuario que solicita el dashboard
   * @returns {Promise<Object>} Datos compilados del dashboard
   */
  async getDashboardData(dashboardId, params = {}, userId) {
    this.logger.info(`[AnalyticsUseCase] Solicitando dashboard ${dashboardId}`, {
      dashboardId,
      userId,
      params
    });

    // Clave de caché única por usuario, dashboard y parámetros
    const cacheKey = this._generateCacheKey('dashboard', dashboardId, userId, params);
    
    try {
      // Intentar obtener de caché primero si está disponible
      if (this.cache) {
        const cachedData = await this.cache.get(cacheKey);
        if (cachedData) {
          this.logger.debug('[AnalyticsUseCase] Datos obtenidos de caché', { dashboardId });
          return cachedData;
        }
      }
      
      // Obtener datos frescos del repositorio
      const dashboardData = await this.analyticsRepository.getDashboard(dashboardId, params);
      
      // Enriquecer con metadatos y transformar según reglas de negocio
      const enrichedData = this._enrichDashboardData(dashboardData, userId);
      
      // Guardar en caché con TTL apropiado según volatilidad de datos
      if (this.cache) {
        const ttl = this._calculateOptimalCacheTtl(dashboardId, params);
        await this.cache.set(cacheKey, enrichedData, ttl);
      }
      
      return enrichedData;
    } catch (error) {
      this.logger.error(`[AnalyticsUseCase] Error al obtener dashboard ${dashboardId}`, {
        error: error.message,
        stack: error.stack,
        dashboardId,
        userId
      });
      
      // Propagar error enriquecido con contexto
      throw new Error(`Error al cargar dashboard: ${error.message}`);
    }
  }
  
  /**
   * Genera un reporte analítico basado en parámetros especificados
   * @param {string} reportType - Tipo de reporte a generar
   * @param {Object} params - Parámetros de configuración
   * @returns {Promise<AnalyticsReport>} Entidad del reporte generado
   */
  async generateReport(reportType, params) {
    this.logger.info(`[AnalyticsUseCase] Generando reporte tipo ${reportType}`, { params });
    
    try {
      // Validar parámetros a nivel aplicación 
      this._validateReportParams(reportType, params);
      
      // Obtener datos crudos del repositorio
      const reportData = await this.analyticsRepository.getReport(reportType, params);
      
      // Crear y devolver entidad inmutable de dominio
      const report = new AnalyticsReport({
        id: reportData.id || `report_${Date.now()}`,
        type: reportType,
        title: params.title || `Reporte de ${reportType}`,
        timeRange: params.timeRange,
        startDate: params.startDate,
        endDate: params.endDate,
        metrics: reportData.metrics,
        dimensions: reportData.dimensions,
        filters: params.filters || {},
        metadata: {
          generatedAt: new Date(),
          dataPoints: reportData.dataPoints?.length,
          ...reportData.metadata
        }
      });
      
      return report;
    } catch (error) {
      this.logger.error(`[AnalyticsUseCase] Error al generar reporte ${reportType}`, {
        error: error.message,
        stack: error.stack,
        reportType,
        params
      });
      
      throw new Error(`Error al generar reporte: ${error.message}`);
    }
  }
  
  /**
   * Detecta anomalías en métricas clave para alertas proactivas
   * @param {Array<string>} metrics - Lista de métricas a monitorear
   * @param {Object} params - Parámetros de configuración
   * @returns {Promise<Array>} Lista de anomalías detectadas
   */
  async detectAnomalies(metrics, params) {
    this.logger.info('[AnalyticsUseCase] Detectando anomalías', { metrics, params });
    
    const results = [];
    const promises = metrics.map(metric => 
      this.analyticsRepository
        .detectAnomalies(metric, params)
        .then(anomalies => ({ metric, anomalies }))
        .catch(error => {
          this.logger.warn(`Error al detectar anomalías para ${metric}`, { error: error.message });
          return { metric, anomalies: [], error: error.message };
        })
    );
    
    try {
      const anomalyResults = await Promise.all(promises);
      
      // Filtrar solo resultados con anomalías reales
      anomalyResults.forEach(result => {
        if (result.anomalies && result.anomalies.length > 0) {
          results.push(result);
        }
      });
      
      return results;
    } catch (error) {
      this.logger.error('[AnalyticsUseCase] Error al procesar detección de anomalías', {
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Error al detectar anomalías: ${error.message}`);
    }
  }
  
  /**
   * Guarda preferencias del usuario para visualizaciones
   * @param {string} userId - ID del usuario
   * @param {Object} preferences - Preferencias a guardar
   * @returns {Promise<boolean>} Resultado de la operación
   */
  async saveUserPreferences(userId, preferences) {
    this.logger.info('[AnalyticsUseCase] Guardando preferencias de usuario', { userId });
    
    try {
      // Validar estructura de preferencias
      this._validateUserPreferences(preferences);
      
      // Persistir preferencias mediante repositorio
      const result = await this.analyticsRepository.saveUserPreferences(userId, preferences);
      
      // Invalidar caché relacionada
      if (this.cache) {
        await this.cache.invalidatePattern(`user:${userId}:*`);
      }
      
      return result;
    } catch (error) {
      this.logger.error(`[AnalyticsUseCase] Error al guardar preferencias para usuario ${userId}`, {
        error: error.message,
        stack: error.stack
      });
      
      throw new Error(`Error al guardar preferencias: ${error.message}`);
    }
  }
  
  /**
   * Realiza una consulta en lenguaje natural al sistema analítico
   * @param {string} question - Pregunta en lenguaje natural
   * @param {Object} context - Contexto adicional (usuario, negocio)
   * @returns {Promise<Object>} Respuesta interpretada con visualizaciones
   */
  async askQuestion(question, context = {}) {
    this.logger.info('[AnalyticsUseCase] Procesando consulta en lenguaje natural', {
      question: question.substring(0, 100), // Limitar tamaño en logs
      userId: context.userId
    });
    
    try {
      return await this.analyticsRepository.askNaturalLanguageQuestion(question, context);
    } catch (error) {
      this.logger.error('[AnalyticsUseCase] Error al procesar consulta natural', {
        error: error.message,
        stack: error.stack,
        question: question.substring(0, 100)
      });
      
      throw new Error(`No pudimos entender la consulta: ${error.message}`);
    }
  }
  
  // Métodos privados para encapsular lógica interna
  
  /**
   * Genera clave única para cache
   * @private
   * @param {string} type - Tipo de recurso
   * @param {string} id - Identificador del recurso
   * @param {string} userId - ID del usuario
   * @param {Object} params - Parámetros que afectan al resultado
   * @returns {string} Clave de cache única
   */
  _generateCacheKey(type, id, userId, params = {}) {
    // Serializar parámetros de manera determinística para clave consistente
    const serializedParams = JSON.stringify(params, Object.keys(params).sort());
    return `${type}:${id}:${userId}:${this._hashString(serializedParams)}`;
  }
  
  /**
   * Función de hash simple para strings
   * @private
   * @param {string} str - String a hashear
   * @returns {string} Hash resultante
   */
  _hashString(str) {
    let hash = 0;
    if (str.length === 0) return hash.toString();
    
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash &= hash; // Convertir a 32bit integer
    }
    
    return Math.abs(hash).toString(16);
  }
  
  /**
   * Calcula TTL óptimo para cache según tipo de datos
   * @private
   * @param {string} dashboardId - ID del dashboard
   * @param {Object} params - Parámetros que afectan volatilidad
   * @returns {number} TTL en segundos
   */
  _calculateOptimalCacheTtl(dashboardId, params) {
    // Datos históricos pueden cachearse por más tiempo
    if (params.timeRange === 'last_month' || params.timeRange === 'last_year') {
      return 3600; // 1 hora
    }
    
    // Datos del día actual son más volátiles
    if (params.timeRange === 'today') {
      return 300; // 5 minutos
    }
    
    // Por defecto 15 minutos
    return 900;
  }
  
  /**
   * Enriquece datos del dashboard con metadatos y personalizaciones
   * @private
   * @param {Object} dashboardData - Datos crudos del dashboard
   * @param {string} userId - ID del usuario para personalizaciones
   * @returns {Object} Datos enriquecidos listos para presentación
   */
  _enrichDashboardData(dashboardData, userId) {
    // Aquí se aplicarían reglas de negocio específicas para enriquecimiento
    // Por simplicidad, solo agregamos metadata básica
    return {
      ...dashboardData,
      metadata: {
        ...dashboardData.metadata,
        processedAt: new Date().toISOString(),
        userId
      }
    };
  }
  
  /**
   * Valida parámetros para generación de reportes
   * @private
   * @param {string} reportType - Tipo de reporte
   * @param {Object} params - Parámetros a validar
   * @throws {Error} Si los parámetros son inválidos
   */
  _validateReportParams(reportType, params) {
    if (!reportType) {
      throw new Error('Se requiere especificar el tipo de reporte');
    }
    
    if (params.timeRange === 'custom') {
      if (!params.startDate || !params.endDate) {
        throw new Error('Rangos personalizados requieren fechas de inicio y fin');
      }
      
      const startDate = new Date(params.startDate);
      const endDate = new Date(params.endDate);
      
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        throw new Error('Fechas de inicio/fin inválidas');
      }
      
      if (startDate > endDate) {
        throw new Error('La fecha de inicio debe ser anterior a la fecha de fin');
      }
    }
  }
  
  /**
   * Valida estructura de preferencias de usuario
   * @private
   * @param {Object} preferences - Preferencias a validar
   * @throws {Error} Si las preferencias tienen formato inválido
   */
  _validateUserPreferences(preferences) {
    if (!preferences || typeof preferences !== 'object') {
      throw new Error('Las preferencias deben ser un objeto válido');
    }
    
    // Aquí podrían validarse campos específicos según esquema
  }
}

// Exportado con ES Modules (export default) al inicio de la clase
